<!-- How the game works. THIS FILE IS YOURS.

     Deliberately separate from the Topics screen, which is where preferences
     live. This answers "what am I meant to do", not "how do I want it to
     behave" — and an eleven-year-old will read exactly as much of it as the
     first line earns, so every line is one rule, in the app's own voice.

     The mode names and emoji come from game/modes.js rather than being typed
     again here: a mode renamed in one place and not the other is a help sheet
     that lies, and this is the one screen that cannot afford to. The RULES
     below are help-specific on purpose — the tiles on Play sell each mode, and
     this says what it actually costs you.

     IT OPENS AT FULL HEIGHT. The chassis stub was a 60% sheet because it was
     three lines long; this is a document. An Ionic sheet only lets its content
     scroll at its LARGEST breakpoint — below that a drag moves the sheet
     instead — so a half-height sheet holding eight modes is a screen where the
     bottom half of the writing cannot be reached by the obvious gesture.
     `[0, 1]` keeps swipe-to-dismiss and gives the content its scroll back. -->

<template>
  <IonModal
    :is-open="modelValue"
    :initial-breakpoint="1"
    :breakpoints="[0, 1]"
    @didDismiss="emit('update:modelValue', false)"
  >
    <IonHeader>
      <IonToolbar>
        <IonTitle>How to play</IonTitle>
        <IonButtons slot="end">
          <IonButton @click="emit('update:modelValue', false)">Done</IonButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>

    <IonContent>
      <p class="ion-padding help-intro">
        {{ APP_TAGLINE }} Seven ways to play, none of them gentle. Here is what
        each one wants from you, and the three rules that run through the lot.
      </p>

      <IonList :inset="true">
        <IonListHeader>
          <IonLabel><h3>The rules</h3><p>These never change.</p></IonLabel>
        </IonListHeader>

        <IonItem>
          <span slot="start" class="help-glyph" aria-hidden="true">♥♥♥</span>
          <IonLabel class="ion-text-wrap">
            <h3>Three lives</h3>
            <p>
              Three hearts in the bar at the top. A wrong answer spends one, and
              so does letting the clock run out. Spend all three and that is
              your lot.
            </p>
          </IonLabel>
        </IonItem>

        <IonItem>
          <span slot="start" class="help-glyph" aria-hidden="true">🔥</span>
          <IonLabel class="ion-text-wrap">
            <h3>Streaks pay</h3>
            <p>
              Right answers in a row are worth more and more, up to double.
              Get one wrong and the streak goes back to nothing.
            </p>
          </IonLabel>
        </IonItem>

        <IonItem>
          <span slot="start" class="help-glyph" aria-hidden="true">🇦🇺</span>
          <IonLabel class="ion-text-wrap">
            <h3>THE AUS BONUS</h3>
            <p>
              Any time AUSTRALIA or AUSTRIA is the right answer, the whole
              screen goes off and the points DOUBLE. It only fires when that
              answer is actually the correct one — picking it and being wrong
              gets you nothing but a shrug.
            </p>
          </IonLabel>
        </IonItem>
      </IonList>

      <IonList :inset="true">
        <IonListHeader>
          <IonLabel>
            <h3>Seven ways to play</h3>
            <p>All from the Play tab. Every one of them feeds the same total.</p>
          </IonLabel>
        </IonListHeader>

        <IonItem v-for="m in playModes" :key="m.id">
          <span slot="start" class="help-glyph" aria-hidden="true">{{ m.emoji }}</span>
          <IonLabel class="ion-text-wrap">
            <h3>{{ m.name }}</h3>
            <p>{{ RULES[m.id] }}</p>
          </IonLabel>
        </IonItem>
      </IonList>

      <IonList :inset="true">
        <IonListHeader>
          <IonLabel><h3>And then there is Endurance</h3><p>The big one.</p></IonLabel>
        </IonListHeader>

        <IonItem>
          <span slot="start" class="help-glyph" aria-hidden="true">{{ ENDURANCE_MODE.emoji }}</span>
          <IonLabel class="ion-text-wrap">
            <h3>{{ ENDURANCE_MODE.name }}</h3>
            <p>{{ RULES.endurance }}</p>
          </IonLabel>
        </IonItem>
      </IonList>

      <p class="ion-padding help-foot">
        Tap the sliders on the Play screen to switch topics off or raise how hard
        it starts. Everything else you unlock by playing — the trophy cabinet is
        watching. Now go and get Austin.
      </p>
    </IonContent>
  </IonModal>
</template>

<script setup>
import {
  IonModal, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton,
  IonList, IonListHeader, IonItem, IonLabel,
} from '@ionic/vue';
import { APP_TAGLINE } from '../../app-config.js';
import { MODES, DAILY_MODE, ENDURANCE_MODE } from '../game/modes.js';

defineProps({ modelValue: Boolean });
const emit = defineEmits(['update:modelValue']);

// The six picker modes plus the Daily, which is a tab rather than a tile but is
// still one of the seven styles of play.
const playModes = [...MODES, DAILY_MODE];

// What each mode COSTS you, keyed by mode id. Kept beside the copy it belongs
// to rather than in modes.js: the tiles on Play need one short boast, and this
// screen needs the timings and the penalty, and cramming both into one string
// makes the tiles unreadable.
const RULES = {
  'chain-reaction':
    'Every answer has to link to the last one — a country, then a player from '
    + 'there, then his club. Twelve seconds a link. A wrong one drops the whole '
    + 'chain and costs a life.',
  'higher-lower':
    'Two things. Tap the bigger, faster, older or heavier one. Eight seconds, '
    + 'which is not thinking time.',
  'odd-one-out':
    'Four things, one impostor. Spot it, then say WHY for double. Fifteen '
    + 'seconds a go, and it gets sneaky.',
  'last-gasp':
    'Sixty seconds of true or false. This is the one mode with no lives: every '
    + 'wrong answer takes FIVE SECONDS off the clock instead. Swipe right for '
    + 'true, left for false, or just tap.',
  'guess-the-thing':
    'A blurry thing that sharpens one clue at a time. Guess on the first clue '
    + 'for triple points. Wait for all five and it is barely worth having.',
  'sort-it':
    'Five things, one right order. Nudge them up and down and lock it in inside '
    + 'forty-five seconds. Every one in the right place scores, so a near miss '
    + 'still counts.',
  daily:
    'Five questions, the same five everybody gets, one attempt. Three right '
    + 'keeps your day streak alive. A fresh one lands tomorrow.',
  endurance:
    'It never stops. Questions from every mode, coming faster and harder — '
    + 'warm-up, sweaty, then FULLY AUSTINATED. Three lives, and running the '
    + 'clock down spends one too. Your score is a TIME: how long you lasted '
    + 'before the legs went.',
};
</script>

<style scoped>
.help-intro {
  margin: 0;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--ink);
}

.help-foot {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--dim);
}

/* Sized as an object rather than as text: these sit beside a heading and a
   two-line paragraph, and at body size they read as punctuation. The step is
   `--text-2xl`, not the 1.5rem literal that was here — same rendered size, but
   it now moves with the scale instead of drifting away from it. */
.help-glyph {
  font-size: var(--text-2xl);
  line-height: 1;
  margin-inline-end: var(--sp-3);
  align-self: center;
}
</style>
